import React, { useState, useMemo } from 'react';
import { ArrowLeft, ChevronRight, Star, MessageSquare, Calendar, RefreshCw, Users } from 'lucide-react';
import { useContent, normalizeContentLang } from '../services/contentService';
import { useI18n } from '../i18n';
import BackgroundStack from './background/BackgroundStack';
import LanguageSelector from './LanguageSelector';

const SKILL_ICONS = {
    default: Star,
    liderazgo: Star,
    comunicacion: MessageSquare,
    'comunicación y escucha activa': MessageSquare,
    planificacion: Calendar,
    'planificación y gestión del tiempo': Calendar,
    flexibilidad: RefreshCw,
    negociacion: Users,
};

const SKILL_ICON_COLORS = [
    { bg: '#1A4731', color: '#6EE7B7' },
    { bg: '#1E3A5F', color: '#93C5FD' },
    { bg: '#4A2C0A', color: '#FCD34D' },
    { bg: '#2D5016', color: '#A3E635' },
    { bg: '#3B1F5E', color: '#C4B5FD' },
];

function SkillSelector({ selectedSkills, onSkillsChange, onContinue, onBack, scenarioInfo, selectedLevel, selectedLanguage, onLanguageChange }) {
    const { content } = useContent();
    const { t } = useI18n();
    const contentSlice = useMemo(() => {
        if (!content?.byLang) return content;
        const L = normalizeContentLang(selectedLanguage);
        return content.byLang[L] || content.byLang.es;
    }, [content, selectedLanguage]);
    const LEVEL_LABELS = { 1: t('basic'), 2: t('intermediate'), 3: t('advanced') };
    const HABILIDADES_DISPONIBLES = contentSlice?.skills || {};
    const [localSelectedSkills, setLocalSelectedSkills] = useState(selectedSkills || []);

    const skillEntries = Object.entries(HABILIDADES_DISPONIBLES);

    const handleSkillToggle = (skillKey) => {
        const newSelection = localSelectedSkills.includes(skillKey) ? [] : [skillKey];
        setLocalSelectedSkills(newSelection);
    };

    const handleContinue = () => {
        onSkillsChange(localSelectedSkills);
        onContinue();
    };

    const isContinueDisabled = localSelectedSkills.length < 1;
    const selectedSkillData = localSelectedSkills.length > 0
        ? HABILIDADES_DISPONIBLES[localSelectedSkills[0]]
        : null;

    return (
        <BackgroundStack className="min-h-screen w-full" sidebarExpanded={false} scrollable>

            {/* Header */}
            <div className="bg-white border-b border-stone-200">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#1A4731' }}>
                                <span className="text-white font-bold text-sm">S</span>
                            </div>
                            <div>
                                <span className="font-semibold text-stone-800 text-sm">SimAI</span>
                                <span className="text-stone-400 text-sm mx-2">|</span>
                                <span className="text-stone-500 text-sm">{t('idbAse')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedLanguage && onLanguageChange && (
                            <LanguageSelector selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />
                        )}
                        <button
                            onClick={onBack}
                            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-stone-100"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('back')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Progress */}
            <div className="bg-white border-b border-stone-200 px-6 py-3">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4">
                        <span className="text-xs text-stone-500 whitespace-nowrap">{t('stepXofY', { x: 2, y: 3 })}</span>
                        <div className="flex-1 flex items-center gap-0.5">
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#1A4731' }} />
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#1A4731' }} />
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#D6D0C4' }} />
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="text-stone-400">{t('scenario')}</span>
                            <span className="font-semibold text-stone-800">{t('skill')}</span>
                            <span className="text-stone-400">{t('simulation')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-8 pb-32">

                {/* Context pill */}
                {(scenarioInfo || selectedLevel) && (
                    <div className="mb-6 flex justify-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-stone-200 shadow-sm">
                            {scenarioInfo?.tipo_proyecto && (
                                <span className="text-[11px] font-semibold text-stone-600 uppercase tracking-wider">
                                    {scenarioInfo.tipo_proyecto.split('–')[0].split('-')[0].trim()}
                                </span>
                            )}
                            {selectedLevel && (
                                <>
                                    <span className="text-stone-300">·</span>
                                    <span className="text-[11px] font-semibold text-stone-600 uppercase tracking-wider">
                                        {LEVEL_LABELS[selectedLevel]}
                                    </span>
                                </>
                            )}
                            {scenarioInfo?.nombre && (
                                <>
                                    <span className="text-stone-300">·</span>
                                    <span className="text-[11px] font-semibold text-stone-600 uppercase tracking-wider truncate max-w-48">
                                        {scenarioInfo.nombre.toUpperCase()}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-stone-900 mb-2">{t('chooseSkill')}</h1>
                    <p className="text-stone-500 text-sm">{t('chooseSkillDesc')}</p>
                </div>

                {/* Skill cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                    {skillEntries.map(([key, skill], index) => {
                        const isSelected = localSelectedSkills.includes(key);
                        const iconColors = SKILL_ICON_COLORS[index % SKILL_ICON_COLORS.length];
                        const normalizedKey = key.toLowerCase();
                        const IconComponent = SKILL_ICONS[normalizedKey] || SKILL_ICONS[Object.keys(SKILL_ICONS).find(k => normalizedKey.includes(k))] || Star;

                        return (
                            <button
                                key={key}
                                onClick={() => handleSkillToggle(key)}
                                className={`relative text-left rounded-2xl p-4 border-2 transition-all duration-200 ${
                                    isSelected
                                        ? 'border-stone-700 bg-white shadow-lg'
                                        : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm'
                                }`}
                            >
                                {isSelected && (
                                    <div
                                        className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full"
                                        style={{ background: '#1A4731' }}
                                    />
                                )}
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                                    style={{ background: iconColors.bg }}
                                >
                                    <IconComponent className="w-5 h-5" style={{ color: iconColors.color }} />
                                </div>
                                <p className="font-semibold text-stone-800 text-sm leading-snug">{skill.nombre}</p>
                            </button>
                        );
                    })}
                </div>

                {/* Description panel — aparece al seleccionar */}
                {selectedSkillData && (() => {
                    const raw = selectedSkillData.descripcion || '';

                    // Separadores flexibles: maneja prefijo " - " o sin él, mayúsculas/minúsculas, ES/EN
                    const sarasRe = /\s*-?\s*(?:Aplicado al SARAS|Applied to SARAS|APLICADO AL SARAS|APPLIED TO SARAS)\s*:/i;
                    const ejRe    = /\s*-?\s*(?:Ejemplos esperados|Expected examples|EJEMPLOS ESPERADOS|EXPECTED EXAMPLES)\s*:/i;
                    const defPre  = /^(?:Definici[oó]n|Definition)\s*:\s*/i;

                    const sarasIdx = raw.search(sarasRe);
                    const ejIdx    = raw.search(ejRe);

                    let definition = sarasIdx > -1 ? raw.slice(0, sarasIdx)
                        : ejIdx > -1 ? raw.slice(0, ejIdx)
                        : raw;
                    definition = definition.replace(defPre, '').trim();

                    const saras = sarasIdx > -1
                        ? raw.slice(sarasIdx, ejIdx > sarasIdx ? ejIdx : undefined)
                            .replace(sarasRe, '').trim()
                        : null;

                    const ejemplosRaw = ejIdx > -1
                        ? raw.slice(ejIdx).replace(ejRe, '').trim()
                        : null;
                    const ejemplosList = ejemplosRaw
                        ? ejemplosRaw.split(/\.\s*-\s*|\n/).map(s => s.replace(/^-\s*/, '').trim()).filter(Boolean)
                        : [];

                    const index = skillEntries.findIndex(([k]) => k === localSelectedSkills[0]);
                    const iconColors = SKILL_ICON_COLORS[index % SKILL_ICON_COLORS.length] || SKILL_ICON_COLORS[0];
                    const normalizedKey = localSelectedSkills[0]?.toLowerCase() || '';
                    const IconComponent = SKILL_ICONS[normalizedKey] || SKILL_ICONS[Object.keys(SKILL_ICONS).find(k => normalizedKey.includes(k))] || Star;

                    return (
                        <div
                            className="mb-6 rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
                            style={{ animation: 'fadeIn 0.25s ease' }}
                        >
                            {/* Encabezado de la descripción */}
                            <div className="flex items-center gap-3 px-6 py-4 border-b border-stone-100">
                                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: iconColors.bg }}>
                                    <IconComponent className="w-4 h-4" style={{ color: iconColors.color }} />
                                </div>
                                <div>
                                    <p className="font-semibold text-stone-900 text-sm">{selectedSkillData.nombre}</p>
                                    <p className="text-[11px] text-stone-400 uppercase tracking-wider">{t('skill')}</p>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-stone-100">
                                {/* Definición */}
                                <div className="px-5 py-4">
                                    <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">{t('definition') || 'Definición'}</p>
                                    <p className="text-sm text-stone-700 leading-relaxed">{definition}</p>
                                </div>

                                {/* Aplicado al SARAS */}
                                {saras && (
                                    <div className="px-5 py-4">
                                        <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">{t('appliedToSARAS') || 'Aplicado al SARAS'}</p>
                                        <p className="text-sm text-stone-700 leading-relaxed">{saras}</p>
                                    </div>
                                )}

                                {/* Ejemplos */}
                                {ejemplosList.length > 0 && (
                                    <div className="px-5 py-4">
                                        <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">{t('expectedExamples') || 'Ejemplos esperados'}</p>
                                        <ul className="space-y-1.5">
                                            {ejemplosList.map((ej, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                                                    <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: iconColors.bg }} />
                                                    <span className="leading-relaxed">{ej}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Bottom summary bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-10">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    {!isContinueDisabled && selectedSkillData ? (
                        <>
                            <div className="flex items-center gap-3 flex-1">
                                {(() => {
                                    const index = skillEntries.findIndex(([k]) => k === localSelectedSkills[0]);
                                    const iconColors = SKILL_ICON_COLORS[index % SKILL_ICON_COLORS.length] || SKILL_ICON_COLORS[0];
                                    const normalizedKey = localSelectedSkills[0]?.toLowerCase() || '';
                                    const IconComponent = SKILL_ICONS[normalizedKey] || SKILL_ICONS[Object.keys(SKILL_ICONS).find(k => normalizedKey.includes(k))] || Star;
                                    return (
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: iconColors.bg }}>
                                            <IconComponent className="w-4 h-4" style={{ color: iconColors.color }} />
                                        </div>
                                    );
                                })()}
                                <div>
                                    <p className="text-sm font-semibold text-stone-800">{selectedSkillData.nombre}</p>
                                    <p className="text-xs text-stone-400">{t('challenges', { n: 2 })} · {selectedLevel ? LEVEL_LABELS[selectedLevel] + ' · ' : ''}{t('voiceAndText')}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleContinue}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:scale-[1.02] shrink-0"
                                style={{ background: '#1A4731' }}
                            >
                                {t('startSimulation')}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <p className="text-sm text-stone-400 flex-1">{t('selectSkillToContinue')}</p>
                    )}
                </div>
            </div>
        </BackgroundStack>
    );
}

export default SkillSelector;
